import { Channel } from '@at/sauron-platform-core'
import React, { FC } from 'react'
import { useForm } from '../../forms/hocs/form.context'
import { ctaMessageBuilder } from '../../utils/search/cta-message-builder'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'
import { PostcodeError } from '../postcode-error/postcode-error'
import { ZeroResults } from '../zero-results/zero-results.hoc'
import { dataAttrGUI, dataAttrTestId } from '@at/formatter-utils'
import { Button } from '@at/design-system-elements'
import { Error } from '../../contexts/user-location-context'
import { StyledNewFormActionArea } from './form-action-area.styles'
import { focusOnElement } from '../../utils/focus-on-element'
import { setSearchId } from '../../utils/set-search-id'
import { useTrackingHandlers } from '../../hooks/tracking/tracking-handlers'
import { useFormState } from '../../forms/hocs/form.state'

export const NewFormActionArea: FC<{
    channel: Channel
    toggleSearchFilter: (value: boolean) => void
    hasPostcodeError: Error
}> = ({ channel, toggleSearchFilter, hasPostcodeError }) => {
    const { filterNumber, totalResults, clearSearch, setLocalFilterPills } = useForm()
    const isClearButton = filterNumber > 0
    const noResults = totalResults === 0

    const totalResultsMessage = ctaMessageBuilder(totalResults, channel)

    const { trackSearchFilterReset } = useTrackingHandlers()

    const searchFormSearchWithTrackingHandler = useATTrackInteraction({
        trackingLabel: `search form search ${channel} button`,
    })
    const { collapseFilterGroups } = useFormState()

    if (hasPostcodeError) {
        return (
            <StyledNewFormActionArea data-testid="action-area">
                <PostcodeError />
            </StyledNewFormActionArea>
        )
    }

    if (totalResults === 0) {
        return (
            <StyledNewFormActionArea data-testid="action-area">
                <ZeroResults />
            </StyledNewFormActionArea>
        )
    }
    return (
        <StyledNewFormActionArea data-testid="search-filters-action-area">
            <Button
                className="search-form-reset"
                displayType="tertiary"
                text={noResults ? 'Undo last filter' : 'Clear all'}
                underline={isClearButton}
                type="button"
                onInteraction={() => {
                    setLocalFilterPills([])
                    clearSearch()
                    setSearchId()
                }}
                trackingCallback={trackSearchFilterReset}
                attributes={{ [`${dataAttrGUI}`]: 'search-reset-button' }}
                disabled={!isClearButton}
            />

            <Button
                text={totalResultsMessage}
                onInteraction={() => {
                    searchFormSearchWithTrackingHandler()
                    window.scrollTo({ top: 0, behavior: 'instant' })
                    toggleSearchFilter(false)
                    focusOnElement('search-filter-toggle')
                    collapseFilterGroups()
                }}
                attributes={{
                    [`${dataAttrGUI}`]: 'search-apply-button',
                    [`${dataAttrTestId}`]: 'search-apply-button',
                    'aria-label': 'Search',
                }}
                disabled={totalResults === 0}
            />
        </StyledNewFormActionArea>
    )
}
